<script>
import baseMixin from '@/mixins/baseMixin'
import { mapGetters } from 'vuex'
import { isEqual, unionWith } from 'lodash'
import BookingSummaryEmptyDayBlock from '@/components/booking/booking-summary-empty-day-block.vue'
import BookingSummaryCardList from '@/components/booking/summary/booking-summary-card-list.vue'
import BookingSummarySkeletonLoader from './booking-summary-skeleton-loader.vue'

export default {
  name: 'BookingSummaryContainer',
  components: {
    BookingSummaryEmptyDayBlock,
    BookingSummaryCardList,
    BookingSummarySkeletonLoader,
  },
  mixins: [baseMixin],
  props: {
    bookings: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
    },
    selectedDay: {
      type: Object,
      default: () => null,
    },
  },
  data: () => {
    return {
      accordionClientIdList: [],
      /**
       * Expand all explanation:
       *
       * If `expandAll` is set to true, all expansion panels on the selected day
       * will be set to open.
       * When changing days, all new expansion panels will be set to open as well.
       *
       * If `expandAll` is set to false, all expansion panels on the selected day
       * are closed.
       * When changing days, new expansion panels are not opened.
       *
       * When a panel is opened it is recorded in the accordionClientIdList array.
       * This is so we can track which panels were opened when changing days.
       *
       * When a panel is closed, we look for it in the `accordionClientIdList` array
       * and set the isOpen flag to false.
       * This is so when changing days that same panel is left closed.
       *
       * The value of `expandAll` is recalculated whenever an expansion panel
       * is opened or closed by clicking on the expansion panel itself.
       * If all expansion panels displayed are open, then `expandAll` is set to true,
       * false if they aren't all open.
       *
       * The necessity for all this logic is due to the fact that the expansion
       * panel component works off of array indexes. We need a separate list
       * to track which panels have been opened by the clientId (accordionClientIdList)
       * which is used to repopulate the `accordian` array with the appropriate indexes.
       */
      expandAll: false,
      accordion: [],
    }
  },
  computed: {
    ...mapGetters({
      getOverviewTotalBookingsCountByDay:
        'bookings/getOverviewTotalBookingsCountByDay',
    }),
    selectedDayHasBooking() {
      return this.bookings && this.bookings?.length > 0
    },
    compExpandBtnText() {
      return this.expandAll ? 'Collapse All' : 'Expand All'
    },
    /**
     * Returns a count for how many skeleton loaders should be generated for
     * a specific date. It will throttle the amount of loaders generated if the
     * limit is exceeded.
     */
    compBookingSummarySkeletonLoaderCount() {
      const SKELTON_LOADER_LIMIT = 10

      const bookingsCountOnSelectedDay = this.selectedDay
        ? this.getOverviewTotalBookingsCountByDay(this.selectedDay)
        : 0

      if (!this.loading) return 0
      if (bookingsCountOnSelectedDay === 0) return SKELTON_LOADER_LIMIT

      return bookingsCountOnSelectedDay > SKELTON_LOADER_LIMIT
        ? SKELTON_LOADER_LIMIT
        : bookingsCountOnSelectedDay
    },
    showEmptyDayBlock() {
      return this.selectedDay && !this.selectedDayHasBooking && !this.loading
    },
  },
  watch: {
    bookings() {
      if (this.expandAll) this.expandAllPanels()
      else this.expandPanelsInClientIdList()
    },
  },
  created() {
    this.expandAllPanels()
  },
  methods: {
    viewTodaysBookings() {
      this.$emit('viewTodaysBookings')
    },
    toggleAllExpansionPanels() {
      if (!this.expandAll) this.expandAllPanels()
      else this.collapseAllPanels()
    },
    expandPanelsInClientIdList() {
      const newAccordionList = []

      for (const [index, booking] of this.bookings.entries()) {
        if (
          this.accordionClientIdList.find(
            (clientPanel) => clientPanel.clientId === booking.clientId
          )?.isOpen
        )
          newAccordionList.push(index)
      }

      this.accordion = newAccordionList
    },
    expandAllPanels() {
      const clientIds = this.bookings.map((booking) => ({
        clientId: booking.clientId,
        isOpen: true,
      }))

      this.accordionClientIdList = unionWith(
        this.accordionClientIdList,
        clientIds,
        isEqual
      )

      this.expandPanelsInClientIdList()

      this.expandAll = true
    },
    collapseAllPanels() {
      this.accordion = []
      this.accordionClientIdList = []
      this.expandAll = false
    },
    viewCandidate(candidateId) {
      this.$emit('viewCandidate', candidateId)
    },
    cancelBooking(booking) {
      this.$emit('cancelBooking', booking)
    },
    /**
     * Updates the state of accordionClientIdList by removing the selected panel's
     * associated clientId if it exists (meaning the panel was open but has
     * been closed) or adding the clientId if it doesn't (meaning the panel was
     * closed but has been opened).
     *
     * @param clientId
     */
    updateExpansionPanelState(clientId) {
      const panelFound = this.accordionClientIdList.find(
        (clientPanel) => clientPanel.clientId === clientId
      )

      if (!panelFound) {
        this.accordionClientIdList.push({ clientId, isOpen: true })
      } else {
        panelFound.isOpen = !panelFound.isOpen
      }

      this.expandAll = this.bookings.every(
        (booking) =>
          !!this.accordionClientIdList.find(
            (clientPanel) => clientPanel.clientId === booking.clientId
          )?.isOpen
      )
    },
  },
}
</script>

<template>
  <BookingSummaryEmptyDayBlock
    v-if="showEmptyDayBlock"
    :selected-date="selectedDay"
    @viewTodaysBookings="viewTodaysBookings"
  />
  <div v-else>
    <div class="my-4 d-flex justify-space-between align-center">
      <v-btn color="primary" text outlined @click="toggleAllExpansionPanels"
        ><v-icon left>{{
          expandAll ? 'mdi-collapse-all' : 'mdi-expand-all'
        }}</v-icon>
        {{ compExpandBtnText }}</v-btn
      >
    </div>

    <v-expansion-panels
      v-if="!loading"
      v-model="accordion"
      accordion
      flat
      multiple
      hover
    >
      <v-expansion-panel
        v-for="client of bookings"
        :key="client.clientId"
        class="transparent"
        @click="updateExpansionPanelState(client.clientId)"
      >
        <v-expansion-panel-header>{{
          client.clientName
        }}</v-expansion-panel-header>
        <v-expansion-panel-content>
          <BookingSummaryCardList
            :bookings="client.locations"
            @viewCandidate="viewCandidate"
            @cancelBooking="cancelBooking"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <BookingSummarySkeletonLoader
      v-else
      :count="compBookingSummarySkeletonLoaderCount"
    />
  </div>
</template>
